import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { tokens } from "../../theme";
import { motion } from "framer-motion";
import TopBarOfMarketing from "../../components/TopBarOfMarketing";
import Footer from "../../components/Footer";
import { useTranslation } from "react-i18next";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import TenantService from "../../services/tenantService";
import Seo from "../../seo/Seo";
import { useRecaptchaToken, RECAPTCHA_ACTIONS } from "../../utils/recaptcha";

export default function CorporateLogin({ darkMode, setDarkMode }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('login');
  const { t: tSeo } = useTranslation('seo');
  const tenantService = new TenantService();
  const getRecaptchaToken = useRecaptchaToken();
  const [showPassword, setShowPassword] = useState(false);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validation: Email and password check
    if (!formData.email || !formData.email.trim()) {
      setError(t('corporate.validation.emailRequired'));
      return;
    }
    
    if (!formData.password || !formData.password.trim()) {
      setError(t('corporate.validation.passwordRequired'));
      return;
    }
    
    // Email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('corporate.validation.emailInvalid'));
      return;
    }
    
    try {
      const recaptchaToken = await getRecaptchaToken(RECAPTCHA_ACTIONS.TENANT_LOGIN);
      const response = await tenantService.tenantLogin({
        ...formData,
        recaptchaToken,
        recaptchaAction: RECAPTCHA_ACTIONS.TENANT_LOGIN,
      });
      const { token, tenant } = response.data;

      if (token) {
        localStorage.setItem("tokenTenant", token);
      }

      if (tenant?.id) {
        localStorage.setItem("tenantId", tenant.id);
      }

      if (tenant) {
        localStorage.setItem("tenantInfo", JSON.stringify(tenant));
      }

      localStorage.setItem(
        "tenantRequiresCheckout",
        "false"
      );

      // Nationality'ye göre dil ayarla (yeni giriş yapıldığı için force=true)
      if (tenant?.nationality) {
        const { setLanguageFromNationality } = await import("../../utils/languageFromNationality");
        setLanguageFromNationality(tenant.nationality, true);
      }

      setOpenSnackBar(true);
      setTimeout(() => {
        navigate("/tenant/dashboard");
      }, 600);
    } catch (err) {
      setError(err.response?.data?.error || t('corporate.validation.loginError'));
    }
  };

  return (
    <Box sx={{ 
      width: "100%",
      background: darkMode ? colors.primary[500] : 'white',
      color: darkMode ? 'white' : colors.grey[100],
    }}>
      <Seo
        path="/corporate-login"
        title={tSeo("corporateLogin.title")}
        description={tSeo("corporateLogin.description")}
        keywords={tSeo("corporateLogin.keywords")}
        noindex
      />
      <TopBarOfMarketing darkMode={darkMode} setDarkMode={setDarkMode} />
      <Box
        sx={{
          minHeight: "100vh",
          background: darkMode 
            ? `linear-gradient(135deg, ${colors.primary[600]}, ${colors.greenAccent[700]})`
            : `linear-gradient(135deg, ${colors.primary[400]}, ${colors.greenAccent[500]})`,
          display: "flex",
          flexDirection: "column",
          mt: "64px",
        }}
      >
        <Container maxWidth="sm" sx={{ py: 8, flex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: "24px",
                background: darkMode ? colors.primary[400] : "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                color: darkMode ? 'white' : colors.grey[100],
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  textAlign: "center",
                  mb: 4,
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.blueAccent[400]})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t('corporate.title')}
              </Typography>

              {location.state?.pendingTenantActivation ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  {t("corporate.pendingActivationAfterRegister")}
                </Alert>
              ) : null}

              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 1, 
                      color: darkMode ? "white" : colors.grey[900]
                    }}
                  >
                    {t('corporate.emailLabel')}
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    name="email"
                    placeholder={t('corporate.emailPlaceholder')}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        "&:hover fieldset": {
                          borderColor: colors.greenAccent[500],
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: darkMode ? "white" : colors.grey[900],
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 4 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "white" : colors.grey[900] }}>
                    {t('corporate.passwordLabel')}
                  </Typography>
                  <TextField
                    fullWidth
                    name="password"
                    placeholder={t('corporate.passwordPlaceholder')}
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityOffIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                            ) : (
                              <VisibilityIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        "&:hover fieldset": {
                          borderColor: colors.greenAccent[500],
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: darkMode ? "white" : colors.grey[900],
                      },
                    }}
                  />
                </Box>

                {error && (
                  <Typography
                    color="error"
                    variant="body2"
                    sx={{ mb: 2, textAlign: "center" }}
                  >
                    {error}
                  </Typography>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    background: "white",
                    color: colors.greenAccent[600],
                    borderRadius: "12px",
                    padding: "12px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
                    "&:hover": {
                      background: "rgba(255,255,255,0.95)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  {t('corporate.loginButton')}
                </Button>

                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      cursor: "pointer",
                      color: colors.blueAccent[400],
                      fontWeight: 500,
                      "&:hover": {
                        textDecoration: "underline",
                        color: colors.blueAccent[300],
                      },
                    }}
                    onClick={() => navigate("/forgot-password/tenant")}
                  >
                    {t('corporate.forgotPassword')}
                  </Typography>
                </Box>

                <Typography
                  sx={{ textAlign: "center", mt: 3, color: colors.grey[500] }}
                  variant="body1"
                >
                  {t('corporate.noAccount')}{" "}
                  <span
                    style={{
                      cursor: "pointer",
                      color: colors.greenAccent[500],
                      fontWeight: 600,
                    }}
                    onClick={() => navigate("/tenantRegister")}
                  >
                    {t('corporate.signUp')}
                  </span>
                </Typography>
              </form>
            </Paper>
          </motion.div>
        </Container>

        <Footer />

        <Snackbar
          open={openSnackBar}
          autoHideDuration={3000}
          onClose={() => setOpenSnackBar(false)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <Alert
            onClose={() => setOpenSnackBar(false)}
            severity="success"
            variant="filled"
            sx={{
              width: "100%",
              borderRadius: "12px",
              backgroundColor: colors.greenAccent[500],
            }}
          >
            <Typography variant="body1" color="white">
              {t('corporate.success')}
            </Typography>
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
